/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 * 
 * ============================================================================
 * DESIGN RULES - AI AGENT MODAL
 * ============================================================================
 * 1. Agent modal ALWAYS displays in LIGHT MODE (regardless of app theme)
 * 2. Thought bubble text is DARK GRAY (#374151 / text-gray-700), never white
 * 3. Feedback thumbs buttons are GRAY (text-gray-500) when not selected
 * 4. Web links display with UNDERLINE and DARK BLUE color (#1e40af)
 * 5. Check-in feedback is AUTOMATICALLY saved to Firebase when user clicks Save
 * 6. TOPIC BUTTONS: All topic bubbles use BLACK TEXT (#000000 / text-black) - includes:
 *    - Applied AI for Transformation
 *    - USMC Knowledge Management
 *    - Human Health & Fitness
 *    - Frameworks for Innovation
 *    - AI Blind Spots & Pitfalls
 *    - Governance & Workforce
 *    - KPI & ROI
 *    - Training
 *    - Next Live Q & A
 *    - + Report
 * ============================================================================
 */

import { X, Mic, Send, AlertTriangle, ChevronDown, ThumbsUp, ThumbsDown, ArrowLeft, Settings, Share2, Check, BarChart3, Bot, Brain, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { sections } from "@/app/data/content";
import { ReportIssueModal } from "./ReportIssueModal";
import { ActionableSummary } from "./ActionableSummary";
import { QuickObservationPopup } from "./QuickObservationPopup";
import { RecentActivity } from "./RecentActivity";
import { submitQuickObservation } from "@/lib/quickObservationService";
import { submitCheckIn } from "@/lib/checkInService";
import { submitQuestionLog } from "@/lib/questionLogService";
import { useVideoRotation } from "@/hooks/useVideoRotation";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { useChatMessages, type Message } from "@/hooks/useChatMessages";
import { useRecentActivity } from "@/hooks/useRecentActivity";
import { useNavigation } from "@/contexts/NavigationContext";
import { getFirebaseDB } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { trackVideoView, trackAgentConnection } from "@/utils/analyticsTracking";

interface AIAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  noBackdrop?: boolean;
}

export function AIAgentModal({ isOpen, onClose, noBackdrop = false }: AIAgentModalProps) {
  // Navigation hook
  const { navigate } = useNavigation();
  
  // Video rotation hook
  const { currentVideo, setCurrentVideo, handleVideoEnded, queueVideoForNext, VIDEO_ROTATION } = useVideoRotation();
  const NEW_PERSONAL_BRAND_VIDEO = "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/PrositionsNono/PersonalBrand_Persona.mp4";
  const DEFAULT_RECOMMENDATION = "Enhance training for information systems and intelligence tools by setting measurable standards and coordinating with local authorities for annual updates based on modern capabilities and user feedback. Improve transparency and efficiency in decision-making.\n\nEstablish a support network of modern intelligence analysts, data specialists, trainers, and security experts to share best practices and ensure annual training stays relevant and effective.";
  const DEFAULT_OBSERVATION = "I saw several participants struggling to navigate the information system during the learning event, delaying access to critical information and causing errors. This directly risks data quality, integrity, and availability.";



  // Chat messages hook
  const { messages, addMessage, logConversation, handleFeedback, clearMessages } = useChatMessages();
  
  // Recent activity hook
  const { addActivity } = useRecentActivity();
  
  // Core UI states
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const [videoContainerHeight, setVideoContainerHeight] = useState<number>(300);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  
  // Drag functionality for desktop
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dragHandleRef = useRef<HTMLDivElement>(null);
  
  // Modal & screen states
  const [showCautionForm, setShowCautionForm] = useState(false);
  const [showReviewScreen, setShowReviewScreen] = useState(false);
  const [showSummaryScreen, setShowSummaryScreen] = useState(false);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);
  const [showActionableSummary, setShowActionableSummary] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showFreeQuestionsPopup, setShowFreeQuestionsPopup] = useState(true);
  const [showQuickObservationPopup, setShowQuickObservationPopup] = useState(false);
  const [isRecentActivityOpen, setIsRecentActivityOpen] = useState(false);
  const [showThankYouPopup, setShowThankYouPopup] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Video tracking for analytics
  const [videoStartTime, setVideoStartTime] = useState<number>(0);
  
  // Observation form data (consolidated)
  const [observationData, setObservationData] = useState({
    topic: "AI Bias or Risk",
    text: DEFAULT_OBSERVATION,
    otherTopic: "",
    type: "Event" as "Event" | "Routine",
    pointOfContact: "",
    location: "Jacksonville, NC (Camp Lejeune)",
    sentiment: "",
    eventAcronym: "CPX",
    fromUnit: "G3",
    impactLevel: "",
    studentEID: "",
    agreeToEthicalAgreement: false,
    wantExpertiseOpportunity: false
  });
  
  // Report form data (consolidated)
  const [reportData, setReportData] = useState({
    date: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase(),
    reference: "",
    from: "Rank F Name MI L Name, Billet",
    to: "Operations Officer",
    via: "Rank Name, Billet",
    subject: "",
    referenceText: "",
    body: "",
    emailAddress: ""
  });
  
  const [recommendation, setRecommendation] = useState(DEFAULT_RECOMMENDATION);
  const [submittedObservation, setSubmittedObservation] = useState<string>("");
  const [selectedEmoji, setSelectedEmoji] = useState<{ emoji: string; label: string } | null>(null);
  const [emailAddress, setEmailAddress] = useState("");
  
  // Voice recognition hooks
  const chatVoice = useVoiceRecognition({
    onTranscript: (text) => setInputValue(prev => prev + text)
  });
  
  const observationVoice = useVoiceRecognition({
    onTranscript: (text) => setObservationData(prev => ({ ...prev, text: prev.text + text }))
  });
  
  const recommendationVoice = useVoiceRecognition({
    onTranscript: (text) => setRecommendation(prev => prev + text)
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const expandedVideoRef = useRef<HTMLVideoElement>(null);



  // VIDEO CONTAINER SPECIFICATIONS
  // The following system dynamically calculates container heights based on video aspect ratio
  // to ensure complete video content display without cropping or letterboxing:
  // - Container: Dynamically sized height (200px min, 233.43px max), black background, relative positioning
  // - Video Player: 100% width/height, object-contain (preserves aspect ratio, ZERO cropping)
  // - Playback: autoPlay, playsInline (mobile optimization), controls enabled, NO LOOP (auto-advance to next video)
  // - Auto-Advance: When a video ends, automatically plays the next video in rotation (never repeats same video)
  // - Interaction: Tap/click to expand to larger overlay view
  // - NO CROP RULE: Videos always fit completely within container - aspect ratio preserved, no content cut off
  // - MAX HEIGHT RULE: All videos capped at 233.43px height for consistent default display across all topics

  // Auto-hide popup after 5 seconds
  useEffect(() => {
    if (showFreeQuestionsPopup) {
      const timer = setTimeout(() => {
        setShowFreeQuestionsPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showFreeQuestionsPopup]);

  // Reset to default video when modal opens with rotation logic
  useEffect(() => {
    console.log('🔍 Leadership Agent useEffect triggered - isOpen:', isOpen);
    if (isOpen) {
      setCurrentVideo(NEW_PERSONAL_BRAND_VIDEO);
      // Initialize rotation state with index 0 (Harvard intro is first in rotation)
      localStorage.setItem('videoRotationState', JSON.stringify({
        currentIndex: 0,
        showCount: 1
      }));
      // Check if we should start a fresh chat session
      const shouldStartFresh = localStorage.getItem('startFreshChat');
      console.log('🔍 Checking startFreshChat flag:', shouldStartFresh);
      console.log('🔍 Current messages count:', messages.length);
      if (shouldStartFresh === 'true') {
        // Force the new video for fresh session
        setCurrentVideo(NEW_PERSONAL_BRAND_VIDEO);
        console.log('🔄 Starting fresh chat in Leadership Agent');
        clearMessages(); // Clear all messages to start fresh
        localStorage.removeItem('startFreshChat'); // Clear the flag
      }
      // Track agent connection when modal opens
      trackAgentConnection('Leadership Agent', true);
    }
  }, [isOpen]);
  
  // Track when modal closes
  useEffect(() => {
    if (!isOpen && videoRef.current && videoStartTime > 0) {
      // Track video view duration when modal closes
      const duration = Math.floor(videoRef.current.currentTime - videoStartTime);
      if (duration > 0) {
        trackVideoView('Leadership Agent Video', duration);
      }
      setVideoStartTime(0);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Upgrade modal is now user-triggered only (via Settings icon)
  // No automatic display on modal open

  // Play video when currentVideo changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load(); // Load the new source
      videoRef.current.play().catch(err => {
        console.log("Video autoplay prevented:", err);
      });
    }
    
    // Show NC4ME messages when NC4MEDAVE.forbes.mp4 plays from the rotation
    if (currentVideo === "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4") {
      // Check if these messages have already been shown to avoid duplicates
      const nc4meAlreadyShown = messages.some(msg => 
        msg.content.includes("North Carolina for military employment")
      );
      
      if (!nc4meAlreadyShown) {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const nc4meMessage: Message = {
              role: "assistant",
              content: "North Carolina for military employment\nConnect directly to NC employers through our Career Connection 365 Employment Exchange. [NC4ME](https://www.nc4me.org)"
            };
            addMessage(nc4meMessage);
            setIsTyping(false);
            
            // Show Forbes article 5 seconds after NC4ME message
            setTimeout(() => {
              setIsTyping(true);
              setTimeout(() => {
                const forbesMessage: Message = {
                  role: "assistant",
                  content: "Additional Resource: [The 5 Growth Skills That Matter Most When Working With AI in 2026](https://www.forbes.com/sites/dianehamilton/2026/01/03/the-5-growth-skills-that-matter-most-when-working-with-ai-in-2026) - Forbes article on essential AI skills."
                };
                addMessage(forbesMessage);
                setIsTyping(false);
              }, 1000);
            }, 5000);
          }, 1000);
        }, 500);
      }
    }
  }, [currentVideo]);

  // Check-in: Set default selection to "Satisfied" when check-in opens
  useEffect(() => {
    if (showCheckIn) {
      // Find the "Satisfied" emoji from options
      const satisfiedEmoji = emojiOptions.find(option => option.label === "Satisfied");
      if (satisfiedEmoji) {
        setSelectedEmoji(satisfiedEmoji);
      }
    }
  }, [showCheckIn]);

  // Check-in: Auto-submit after 10 seconds of inactivity
  useEffect(() => {
    if (showCheckIn) {
      const autoSubmitTimer = setTimeout(() => {
        // Auto-submit with current selection (defaults to "Satisfied")
      }, 10000); // 10 seconds

      return () => clearTimeout(autoSubmitTimer);
    }
  }, [showCheckIn, selectedEmoji]); // Reset timer when emoji changes

  // Function to log questions to localStorage AND Supabase
  const logQuestion = async (topic: string, question: string, isPillButton: boolean) => {
    // Log to localStorage
    const stored = localStorage.getItem("agentQuestionLogs");
    const logs = stored ? JSON.parse(stored) : [];
    
    logs.push({
      topic,
      question,
      timestamp: Date.now(),
      isPillButton
    });
    
    localStorage.setItem("agentQuestionLogs", JSON.stringify(logs));
    
    // Also log to Supabase
    const result = await submitQuestionLog({
      topic,
      question,
      isPillButton
    });
    
    // Only log success, silently skip if database not available (dev mode)
    // Only log success, silently skip if database not available (dev mode)
    if (result.success) {
      console.log('✅ Question logged to Supabase with ID:', result.id);
    }
  };



  const aiTopics = [
    "Applied AI for Transformation",
    "Knowledge Base",
    "Human Health & Fitness",
    "Frameworks for Innovation",
    "AI Blind Spots & Pitfalls",
    "Governance & Workforce",
    "KPI & ROI",
    "Training",
    "Next Live Q & A",
    "+ Report"
  ];

  // Map topics to video URLs
  const topicVideoMap: Record<string, string> = {
    "Applied AI for Transformation": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/MIT_Arnold.mp4",
    "Human Health & Fitness": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/KleeKaren2.mp4",
    "Knowledge Base": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/CWO-John2026.mp4",
    "Frameworks for Innovation": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    "AI Blind Spots & Pitfalls": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/Big3Risk-RACI-Governance.mp4",
    "Governance & Workforce": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/SSgtW.mp4",
    "KPI & ROI": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/RACI-AI.mp4",
    "Training": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/Process-Citrus.mp4",
  };
  
  // Keyword to video mapping for user-typed queries
  const keywordVideoMap: Record<string, string> = {
    // Tony/Frameworks keywords → TonyMade.mp4
    "frameworks": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    "innovation": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    "modular": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    "agile": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    "mindset": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    "bureaucracy": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    "tony": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/TonyMade.mp4",
    
    // NC4ME/Veteran keywords → NC4MEDAVE.forbes.mp4
    "veteran": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    "vet": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    "nc": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    "nc4me": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    "processes": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
    "hiring": "https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NC4MEDAVE.forbes.mp4",
  };

  // Search in-app content for relevant information
  const searchContentForAnswer = (query: string): string | null => {
    const lowerQuery = query.toLowerCase();
    
    // Special case for Dashboard, Trust, or KPI queries
    if (lowerQuery.includes("dashboard") || lowerQuery.includes("trust") || lowerQuery.includes("kpi")) {
      return "A simple starting point for AI Governance KPIs can start with transparency and trust.\n\n**Governance Transparency & Accessibility Score (TAS)**\nMeasures how clearly AI governance policies, roles, and KPIs are communicated and how easily employees can access them. Transparency builds trust.\n\n**KPI Definition:**\nTracks the percentage of AI governance materials (policies, standards, KPIs, decision logs, model cards, risk assessments) that are published, up‑to‑date, and accessible through a centralized, easy‑to‑find location.\n\n**Example Target:**\nAchieve 95% publication and accessibility of all current AI governance documents in a single, searchable repository.";
    }
    
    // Special case for ROI queries (standalone, not part of open government context)
    if (lowerQuery.includes("roi") || lowerQuery.includes("return on investment")) {
      return "Return on Investment (ROI) is a simple way to measure how much value you get back compared to what you put in. It's used in business, finance, marketing, training programs, and even personal decisions.";
    }
    
    // Special case for Open Government Act / Budget queries
    if (lowerQuery.includes("open government") || lowerQuery.includes("budget")) {
      return "The Open Government requires Machine-Readable Data — Open Government data assets made available by all agencies. [https://www.congress.gov/bill/115th-congress/house-bill/1770/text](https://www.congress.gov/bill/115th-congress/house-bill/1770/text)";
    }
    
    // Special case for Free AI Leadership Training queries
    if (lowerQuery.includes("free") && (lowerQuery.includes("ai") || lowerQuery.includes("leadership") || lowerQuery.includes("training"))) {
      return "For free AI leadership training, check out: [https://agent.myora.now](https://agent.myora.now)\n\nThis resource provides comprehensive AI leadership development content.";
    }
    
    // Special case for Frameworks or Agile queries
    if (lowerQuery.includes("framework") || lowerQuery.includes("agile")) {
      return "Agile is a way of working that helps teams deliver work faster, more flexibly, and with continuous improvement. It started in software development but is now used across government, business, and even military planning because it adapts quickly to change. I recommend this free course for Agile AI training: [AI Agility: Comprehensive Introduction](https://resources.scrumalliance.org/Course/ai-agility-comprehensive-introduction)";
    }
    
    // Special case for RACI/RACU Matrix queries
    if (lowerQuery.includes("raci") || lowerQuery.includes("racu")) {
      return "The RACI matrix defines roles as Responsible, Accountable, Consulted, and Informed. A RACI Matrix (also called a Responsibility Assignment Matrix) is a project-management tool used to define who does what for every task or deliverable in a project. It assigns one of four roles to each stakeholder. I suggest this RACI template: [https://www.smartsheet.com/content/raci-templates-excel](https://www.smartsheet.com/content/raci-templates-excel)";
    }
    
    // Keywords mapped to section IDs and topics
    const keywordMap: Record<string, string[]> = {
      "persona": ["introduction"],
      "digital persona": ["introduction"],
      "cost": ["ai-costs"],
      "token": ["ai-costs"],
      "energy": ["ai-costs"],
      "ai literacy": ["ai-literacy"],
      "literacy": ["ai-literacy"],
      "bias": ["ai-literacy"],
      "risk": ["ai-literacy"],
      "framework": ["trusted-frameworks"],
      "nist": ["trusted-frameworks"],
      "agile": ["trusted-frameworks"],
      "scrum": ["trusted-frameworks"],
      "notebooklm": ["ai-research"],
      "research": ["ai-research"],
      "google": ["ai-research"],
      "agentic": ["applied-agentic-ai"],
      "agent": ["applied-agentic-ai"],
      "human-in-the-loop": ["applied-agentic-ai"],
      "oversight": ["applied-agentic-ai"],
      "trust": ["build-trust"],
      "responsible": ["build-trust"],
      "accountable": ["build-trust"],
      "kpi": ["kpis-applied-to-operations"],
      "roi": ["kpis-applied-to-operations"],
      "governance": ["kpis-applied-to-operations", "build-trust"],
      "transparency": ["kpis-applied-to-operations"],
      "blockbuster": ["summary"],
      "amazon": ["summary"],
      "drone": ["summary"]
    };

    // Find relevant sections based on keywords
    const relevantSectionIds = new Set<string>();
    
    for (const [keyword, sectionIds] of Object.entries(keywordMap)) {
      if (lowerQuery.includes(keyword)) {
        sectionIds.forEach(id => relevantSectionIds.add(id));
      }
    }

    // If no keyword matches, try to match section titles
    if (relevantSectionIds.size === 0) {
      sections.forEach(section => {
        if (section.title.toLowerCase().includes(lowerQuery) || 
            lowerQuery.includes(section.title.toLowerCase().split(" ")[0])) {
          relevantSectionIds.add(section.id);
        }
      });
    }

    // Build response from relevant sections
    if (relevantSectionIds.size > 0) {
      const relevantSections = sections.filter(s => relevantSectionIds.has(s.id));
      let response = "";

      relevantSections.forEach((section, idx) => {
        if (idx === 0 && section.content.paragraphs && section.content.paragraphs.length > 0) {
          // Use first paragraph from the most relevant section
          response += section.content.paragraphs[0];
          
          // Add a callout if available
          if (section.content.callouts && section.content.callouts.length > 0) {
            const callout = section.content.callouts[0];
            response += `\n\n💡 ${callout.text}`;
            if (callout.source) {
              response += ` (Source: ${callout.source})`;
            }
          }
          
          // Add list items if available
          if (section.content.lists && section.content.lists.length > 0) {
            const list = section.content.lists[0];
            response += `\n\n${list.title}:\n`;
            list.items.slice(0, 2).forEach(item => {
              response += `• ${item}\n`;
            });
          }
        }
      });

      if (response) {
        response += `\n\nWould you like to learn more about this topic?`;
        return response;
      }
    }

    return null;
  };

  // Agent responses based on topics
  const getTopicResponse = (topic: string): string => {
    const responses: Record<string, string> = {
      "Applied AI for Transformation": "Let me share insights from MIT research on AI transformation. Organizations that successfully adopt AI focus on three key areas: workforce readiness, governance frameworks, and measurable outcomes. I recommend this course: [Online Program: Agentic AI & Organizational Transformation](https://professionalprograms.mit.edu/online-program-agentic-ai-organizational-transformation/)",
      "Knowledge Base": "The Knowledge Base approach emphasizes rapid information sharing and decision-making under uncertainty. Their framework integrates AI to enhance situational awareness. I suggest this document: [NAVMC-3000.1SECURED.pdf](https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/make-3504d096-videos/NAVMC-3000.1SECURED.pdf)",
      "Human Health & Fitness": "AI applications in health and fitness range from personalized training to predictive health analytics. Recent studies show AI-driven interventions improve outcomes by 40%. Are you interested in specific applications or research findings? [mitsloan.mit.edu/when-humans-and-ai-work-best-together](https://mitsloan.mit.edu/ideas-made-to-matter/when-humans-and-ai-work-best-together-and-when-each-better-alone) by Brian Eastwood, Feb 3, 2025",
      "Human Health": "AI applications in health and fitness range from personalized training to predictive health analytics. Recent studies show AI-driven interventions improve outcomes by 40%. Are you interested in specific applications or research findings? [mitsloan.mit.edu/when-humans-and-ai-work-best-together](https://mitsloan.mit.edu/ideas-made-to-matter/when-humans-and-ai-work-best-together-and-when-each-better-alone) by Brian Eastwood, Feb 3, 2025",
      "Frameworks for Innovation": "Innovation frameworks provide structured approaches to AI adoption. The most effective combine agile methodologies with design thinking and continuous learning. Would you like to explore specific frameworks?\n\nThis can help any leader ask better AI questions: [Harvard 2025 C-Level Assessment](https://www.harvardbusiness.org/wp-content/uploads/2025/10/CRE6997_ENT_CLevel_Assessment_Oct2025.pdf)\n\nI suggest this resource: [NIST AI Risk Management Framework](https://www.nist.gov/itl/ai-risk-management-framework)",
      "AI Blind Spots & Pitfalls": "Common AI blind spots include bias in training data, over-reliance on automation, and lack of human oversight. Organizations need governance structures to identify and mitigate these risks.\n\nLeaders must ask critical questions: What level of accuracy is truly required? Can lower‑cost models achieve the same outcome? Should systems be air‑gapped for privacy or distributed across multi‑cloud environments for resilience and sustainability? Have we created risk by becoming dependent on consultants or vendor lock-in? What Key Performance Indicators (KPI) will be used to measure AI investment?\n\nHistory shows what happens when organizations fail to evolve. Amazon moved from pilots to drone delivery while competitors hesitated. Blockbuster once had 9,000 stores and $6B in revenue, yet its failure to adapt left only one store standing today.\n\nThe lesson is clear: the greatest danger lies in not evolving. The risks include loss of consumer confidence, loss of funding, and loss of competitive advantage — all of which can be far more costly than adopting AI in the first place. What specific challenges are you facing?\n\nI recommend this IBM resource: [Year of Agentic AI Takes Center Stage in 2025](https://www.ibm.com/think/news/year-agentic-ai-center-stage-2025?utm_source=copilot.com)",
      "Governance & Workforce": "Effective AI governance balances innovation with risk management. Workforce readiness requires upskilling, change management, and clear role definitions using frameworks like RACI. Get a template and get started! [https://www.smartsheet.com/content/raci-templates-excel](https://www.smartsheet.com/content/raci-templates-excel)",
      "ROI": "Return on Investment (ROI) is a simple way to measure how much value you get back compared to what you put in. It's used in business, finance, marketing, training programs, and even personal decisions.",
      "KPI & ROI": "An AI Governance KPI Dashboard builds trust, strengthens transparency and maximizes return on investment by giving leaders a clear view of model reliability, data-drift incidents, fairness audit coverage, and overall operational performance. Key metrics include: Operational Maintenance & Consumption Cost, Model Reliability, Data Drift Incidents, Fairness Audit Coverage, Transparency Measurement, IT Spending on Applied AI, and AI Workforce Training & Readiness. I suggest this resource: [STATE OF AI IN BUSINESS 2025](https://mlq.ai/media/quarterly_decks/v0.1_State_of_AI_in_Business_2025_Report.pdf?utm_source=copilot.com)",
      "Training": "Effective AI training programs combine technical skills with practical application. Research shows that hands-on learning with real-world scenarios increases retention by 60%.\n\nCheck out: [The 5 Skill Sets Leaders Must Develop in the AI Era](https://www.forbes.com/councils/forbescoachescouncil/2026/01/07/the-5-skill-sets-leaders-must-develop-in-the-ai-era/?utm_source=copilot.com) - Forbes article on essential AI skills.\n\nFor free AI leadership training, and a personalized agent like this one, check out: [https://agent.myora.now](https://agent.myora.now)",
      "Next Live Q & A": "Let's make an appointment! Click [Calendly.com/caraz007](https://calendly.com/caraz007)\n\nMy personal AI avatar makes the introduction and helps me stay organized! We can talk about free AI leadership training, Agentic AI, measurable change and learn how you can get a personalized agent just like this one!",
      "+ Report": "Log quick observations to generate actionable reports. Share your leadership insights. Document your findings, monitor and track AI performance. Share recommendations with your team.",
      "Open Government Act": "The Open Government requires Machine-Readable Data — Open Government data assets made available by all agencies. [https://www.congress.gov/bill/115th-congress/house-bill/1770/text](https://www.congress.gov/bill/115th-congress/house-bill/1770/text)"
    };
    return responses[topic] || "Thank you for asking. I am thinking... hmm...";
  };

  // Secondary response for Frameworks topic (shown after 5 seconds)
  const getFrameworksSecondaryResponse = (): string => {
    return "I also recommend this free course for Agile AI training: [AI Agility: Comprehensive Introduction](https://resources.scrumalliance.org/Course/ai-agility-comprehensive-introduction?_gl=1*a0c0tj*_gcl_au*Mzc4Mjc5ODEyLjE3NjcxMDE1NDA.)";
  };

  const handleTopicClick = (topic: string) => {
    const userMessage: Message = { role: "user", content: topic };
    addMessage(userMessage);
    
    // Log question to localStorage for tracking
    logQuestion(topic, topic, true);
    
    // Change video instantly if topic has a mapped video
    if (topicVideoMap[topic]) {
      setCurrentVideo(topicVideoMap[topic]);
    }
    
    setIsTyping(true);
    setTimeout(() => {
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const response = getTopicResponse(topic);
      const assistantMessage: Message = {
        role: "assistant",
        content: response,
        id: messageId,
        timestamp: Date.now(),
        feedback: null
      };
      addMessage(assistantMessage);
      setIsTyping(false);
      
      // Log conversation for analytics
      logConversation(topic, response, topic);

      // Special handling for "+ Report" - open Quick Observation popup
      if (topic === "+ Report") {
        setTimeout(() => {
          setShowQuickObservationPopup(true);
        }, 500);
      }

      // Special handling for Frameworks topic - send a second message after 5 seconds
      if (topic === "Frameworks for Innovation") {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const secondaryMessage: Message = {
              role: "assistant",
              content: getFrameworksSecondaryResponse()
            };
            addMessage(secondaryMessage);
            setIsTyping(false);
          }, 1000);
        }, 5000);
      }

      // Special handling for Applied AI for Transformation - send a second message after 5 seconds
      if (topic === "Applied AI for Transformation") {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const secondaryMessage: Message = {
              role: "assistant",
              content: "I also suggest this IBM training: [Agentic AI in Practice Learning Path](https://www.ibm.com/training/learning-path/agentic-ai-in-practice-1058?utm_source=copilot.com)"
            };
            addMessage(secondaryMessage);
            setIsTyping(false);
          }, 1000);
        }, 5000);
      }

      // Special handling for Training topic - send a second message after 5 seconds
      if (topic === "Training") {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const secondaryMessage: Message = {
              role: "assistant",
              content: "I also suggest this resource for AI leadership & governance readiness: [MIT Sloan Workforce Intelligence](https://mitsloan.mit.edu/sites/default/files/2025-09/MIT%20Sloan%20-%20Workforce%20Intelligence-digital.pdf)"
            };
            addMessage(secondaryMessage);
            setIsTyping(false);
          }, 1000);
        }, 5000);
      }

      // Special handling for Governance & Workforce topic - send a second message after 45 seconds
      if (topic === "Governance & Workforce") {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const secondaryMessage: Message = {
              role: "assistant",
              content: "The AI Governance Mapping project classifies over 900 AI risk governance documents by risk domain and other taxonomies. Suggest [https://airisk.mit.edu/ai-governance](https://airisk.mit.edu/ai-governance)"
            };
            addMessage(secondaryMessage);
            setIsTyping(false);
            
            // Third message - Harvard C-Level Guide
            setTimeout(() => {
              setIsTyping(true);
              setTimeout(() => {
                const tertiaryMessage: Message = {
                  role: "assistant",
                  content: "For AI leadership conversation starters, I recommend this Harvard Business guide: [C-Level Guide to AI Leadership (Oct 2025)](https://www.harvardbusiness.org/wp-content/uploads/2025/10/CRE6706_ENT_LDS25_CLevel_Guide_Oct2025_V4.pdf)"
                };
                addMessage(tertiaryMessage);
                setIsTyping(false);
                
                // Fourth message - White House M-25-05 Reference
                setTimeout(() => {
                  setIsTyping(true);
                  setTimeout(() => {
                    const fourthMessage: Message = {
                      role: "assistant",
                      content: "Real World Reference Materials: [M-25-05: Phase 2 Implementation of the Foundations for Evidence-Based Policymaking Act of 2018](https://www.whitehouse.gov/wp-content/uploads/2025/01/M-25-05-Phase-2-Implementation-of-the-Foundations-for-Evidence-Based-Policymaking-Act-of-2018-Open-Government-Data-Access-and-Management-Guidance.pdf)"
                    };
                    addMessage(fourthMessage);
                    setIsTyping(false);
                  }, 1000);
                }, 5000); // 5 seconds after third message
              }, 1000);
            }, 5000); // 5 seconds after second message
          }, 1000);
        }, 5000); // 5 seconds delay
      }

      // Special handling for AI Blind Spots & Pitfalls topic - send follow-up messages
      if (topic === "AI Blind Spots & Pitfalls") {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const secondaryMessage: Message = {
              role: "assistant",
              content: "I also suggest this World Economic Forum article on AI investment: [AI Investment Opportunities & Risks](https://www.weforum.org/stories/2024/03/ai-investment-opportunities-risks/?utm_source=copilot.com)"
            };
            addMessage(secondaryMessage);
            setIsTyping(false);
            
            // Third message - The Hill podcast
            setTimeout(() => {
              setIsTyping(true);
              setTimeout(() => {
                const tertiaryMessage: Message = {
                  role: "assistant",
                  content: "I also recommend listening to this podcast about generative AI returns: [Generative AI Zero Returns - MIT Report](https://thehill.com/policy/technology/5460663-generative-ai-zero-returns-businesses-mit-report/?utm_source=copilot.com)"
                };
                addMessage(tertiaryMessage);
                setIsTyping(false);
              }, 1000);
            }, 5000); // 5 seconds after second message
          }, 1000);
        }, 5000); // 5 seconds delay
      }

      // Special handling for KPI & ROI topic - send follow-up messages
      if (topic === "KPI & ROI") {
        // First Harvard link - appears immediately
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const harvardGuideMessage: Message = {
              role: "assistant",
              content: "[Harvard C-Suite Guide (Oct 2025)](https://www.harvardbusiness.org/wp-content/uploads/2025/10/CRE6706_ENT_LDS25_CLevel_Guide_Oct2025_V4.pdf)"
            };
            addMessage(harvardGuideMessage);
            addMessage(harvardTemplateMessage);
            setIsTyping(false);

            // TAS message - appears after Harvard template
            setTimeout(() => {
              setIsTyping(true);
              setTimeout(() => {
                const tasMessage: Message = {
                  role: "assistant",
                  content: "A simple starting point for AI Governance KPIs can start with transparency and trust.\n\n**Governance Transparency & Accessibility Score (TAS)**\nMeasures how clearly AI governance policies, roles, and KPIs are communicated and how easily employees can access them. Transparency builds trust.\n\n**KPI Definition:**\nTracks the percentage of AI governance materials (policies, standards, KPIs, decision logs, model cards, risk assessments) that are published, up‑to‑date, and accessible through a centralized, easy‑to‑find location.\n\n**Example Target:**\nAchieve 95% publication and accessibility of all current AI governance documents in a single, searchable repository."
                };
                addMessage(tasMessage);
                setIsTyping(false);
              }, 1000);
            }, 3000); // 3 seconds after Harvard template
          }, 1000);
        }, 2000); // 2 seconds initial delay
        
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const secondaryMessage: Message = {
              role: "assistant",
              content: "For free AI leadership training, check out: [https://agent.myora.now](https://agent.myora.now)"
            };
            addMessage(secondaryMessage);
            setIsTyping(false);
            
            // Third message - Free consultation
            setTimeout(() => {
              setIsTyping(true);
              setTimeout(() => {
                const tertiaryMessage: Message = {
                  role: "assistant",
                  content: "Let's talk when you have questions about developing your AI Governance KPI dashboard, make an appointment! The first consultation is FREE! Click [Calendly.com/caraz007](https://calendly.com/caraz007)"
                };
                addMessage(tertiaryMessage);
                setIsTyping(false);
                
                // Fourth message - White House M-25-05 Reference
                setTimeout(() => {
                  setIsTyping(true);
                  setTimeout(() => {
                    const fourthMessage: Message = {
                      role: "assistant",
                      content: "Real World Reference Materials: [M-25-05: Phase 2 Implementation of the Foundations for Evidence-Based Policymaking Act of 2018](https://www.whitehouse.gov/wp-content/uploads/2025/01/M-25-05-Phase-2-Implementation-of-the-Foundations-for-Evidence-Based-Policymaking-Act-of-2018-Open-Government-Data-Access-and-Management-Guidance.pdf)"
                    };
                    addMessage(fourthMessage);
                    setIsTyping(false);
                  }, 1000);
                }, 5000); // 5 seconds after third message
              }, 1000);
            }, 5000); // 5 seconds after second message
          }, 1000);
        }, 13000); // 13 seconds delay (2 + 6 + 3 + 2 to account for Harvard links and TAS message)
      }

      // Special handling for Knowledge Base topic - send Harvard links
      if (topic === "Knowledge Base") {
        // First Harvard link - appears immediately
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const harvardGuideMessage: Message = {
              role: "assistant",
              content: "[Harvard C-Suite Guide (Oct 2025)](https://www.harvardbusiness.org/wp-content/uploads/2025/10/CRE6706_ENT_LDS25_CLevel_Guide_Oct2025_V4.pdf)"
            };
            addMessage(harvardGuideMessage);
            setIsTyping(false);
            
            // Second Harvard link - appears 6 seconds later
            setTimeout(() => {
              setIsTyping(true);
              setTimeout(() => {
                const harvardTemplateMessage: Message = {
                  role: "assistant",
                  content: "[C-Suite template](https://www.harvardbusiness.org/wp-content/uploads/2025/10/CRE6997_ENT_CLevel_Assessment_Oct2025.pdf)"
                };
                addMessage(harvardTemplateMessage);
                setIsTyping(false);
              }, 1000);
            }, 6000); // 6 seconds delay
          }, 1000);
        }, 2000); // 2 seconds initial delay
      }
      
      // Universal Forbes article suggestion for ALL pill topics
      // This appears after all topic-specific messages
      const delayForForbes = topic === "Governance & Workforce" ? 25000 : 
                              topic === "KPI & ROI" ? 25000 : 
                              topic === "Knowledge Base" ? 12000 : 
                              topic === "AI Blind Spots & Pitfalls" ? 15000 : 
                              topic === "Frameworks for Innovation" || topic === "Applied AI for Transformation" || topic === "Training" ? 10000 : 
                              5000;
      
      setTimeout(() => {
        setIsTyping(true);
        setTimeout(() => {
          const forbesMessage: Message = {
            role: "assistant",
            content: "Additional Resource: [The 5 Growth Skills That Matter Most When Working With AI in 2026](https://www.forbes.com/sites/dianehamilton/2026/01/03/the-5-growth-skills-that-matter-most-when-working-with-ai-in-2026/) - Forbes article on essential AI skills."
          };
          addMessage(forbesMessage);
          setIsTyping(false);
        }, 1000);
      }, delayForForbes);
    }, 1000);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = { 
      role: "user", 
      content: inputValue,
      id: `msg_${Date.now()}_user`,
      timestamp: Date.now()
    };
    addMessage(userMessage);
    
    // Log custom question to localStorage for tracking
    logQuestion("Custom Questions", inputValue, false);
    
    const questionText = inputValue;
    setInputValue("");
    
    // Check if user input contains any keywords that should trigger video change
    // ALL keyword-triggered videos now queue to play after current video ends
    const lowerCaseInput = questionText.toLowerCase();
    
    // Special handling for USMC - trigger the same response as clicking the pill button
    if (lowerCaseInput.includes("usmc")) {
      handleTopicSelect("USMC Knowledge Management");
      return; // Exit early since handleTopicSelect handles everything
    }
    
    for (const [keyword, videoUrl] of Object.entries(keywordVideoMap)) {
      if (lowerCaseInput.includes(keyword)) {
        // Queue video to play after current video ends
        queueVideoForNext(videoUrl);
        break; // Use first matching keyword
      }
    }

    setIsTyping(true);
    setTimeout(() => {
      const searchResult = searchContentForAnswer(questionText);
      const responseContent = searchResult || "Thank you for asking. I am thinking... hmm...";
      const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const assistantMessage: Message = {
        role: "assistant",
        content: responseContent,
        id: messageId,
        timestamp: Date.now(),
        feedback: null
      };
      addMessage(assistantMessage);
      setIsTyping(false);
      
      // Check for Modular Misfits keywords and send follow-up message
      const modularKeywords = ["modular", "agile", "framework", "mindset", "innovation"];
      const containsModularKeyword = modularKeywords.some(keyword => 
        lowerCaseInput.includes(keyword)
      );
      
      if (containsModularKeyword) {
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            const modularMessage: Message = {
              role: "assistant",
              content: "I recommend exploring: [https://modularmisfits.com/](https://modularmisfits.com/)"
            };
            addMessage(modularMessage);
            setIsTyping(false);
          }, 1000);
        }, 3000); // 3 seconds after first response
      }
      
      // Log conversation for analytics
      logConversation(questionText, responseContent);
    }, 1200);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const copyShareLink = () => {
    // Deep link always triggers fresh chat and new video
    localStorage.setItem('startFreshChat', 'true');
    localStorage.setItem('leadershipAgentVideo', 'https://naskxuojfdqcunotdjzi.supabase.co/storage/v1/object/public/PrositionsNono/PersonalBrand_Persona.mp4');
    const shareUrl = `${window.location.origin}${window.location.pathname}?agent=leadership&newChat=true`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Drag handlers for desktop only
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only enable drag on desktop (screen width > 640px)
    if (window.innerWidth <= 640) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🎯 Drag started - mouseDown detected');
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || window.innerWidth <= 640) return;
      
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, position]);

  // Reset position when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen]);

  // Video navigation handlers
  const handlePreviousVideo = () => {
    const currentIndex = VIDEO_ROTATION.indexOf(currentVideo);
    const previousIndex = currentIndex === 0 ? VIDEO_ROTATION.length - 1 : currentIndex - 1;
    const previousVideo = VIDEO_ROTATION[previousIndex];
    
    // Update video and restart
    setCurrentVideo(previousVideo);
    
    // Update localStorage
    localStorage.setItem('videoRotationState', JSON.stringify({
      currentIndex: previousIndex,
      showCount: 1
    }));
    
    // Restart video
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => console.log("Video autoplay prevented:", err));
    }
  };

  const handleNextVideo = () => {
    const currentIndex = VIDEO_ROTATION.indexOf(currentVideo);
    const nextIndex = (currentIndex + 1) % VIDEO_ROTATION.length;
    const nextVideo = VIDEO_ROTATION[nextIndex];
    
    // Update video and restart
    setCurrentVideo(nextVideo);
    
    // Update localStorage
    localStorage.setItem('videoRotationState', JSON.stringify({
      currentIndex: nextIndex,
      showCount: 1
    }));
    
    // Restart video
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => console.log("Video autoplay prevented:", err));
    }
  };

  const showGreeting = messages.length === 0;

  const emojiOptions = [
    { emoji: "😕", label: "Confused" },
    { emoji: "🙂", label: "Happy" },
    { emoji: "😏", label: "Satisfied" },
    { emoji: "🤨", label: "Curious" },
    { emoji: "🤯", label: "Overwhelmed" },
    { emoji: "😎", label: "Confident" },
    { emoji: "😞", label: "Disappointed" },
    { emoji: "😐", label: "Meh" }
  ];

  // Sentiment options for observation form
  const sentimentOptions = [
    { emoji: "😨", label: "Afraid" },
    { emoji: "🤝", label: "Acceptance" },
    { emoji: "🧑", label: "Alone" },
    { emoji: "😈", label: "Angry" },
    { emoji: "😰", label: "Anxious" },
    { emoji: "🥰", label: "Appreciated" },
    { emoji: "😳", label: "Ashamed" },
    { emoji: "😌", label: "Calm" },
    { emoji: "😕", label: "Confused" },
    { emoji: "🧐", label: "Curious" },
    { emoji: "😫", label: "Depleted" },
    { emoji: "😞", label: "Depressed" },
    { emoji: "😞", label: "Disappointed" },
    { emoji: "😳", label: "Embarrassed" },
    { emoji: "��", label: "Empowered" },
    { emoji: "😃", label: "Excited" },
    { emoji: "😤", label: "Frustrated" },
    { emoji: "🙏", label: "Grateful" },
    { emoji: "😢", label: "Grief" },
    { emoji: "😔", label: "Guilty" },
    { emoji: "😊", label: "Happy" },
    { emoji: "🌟", label: "Hopeful" },
    { emoji: "😩", label: "Hopeless" },
    { emoji: "✨", label: "Inspired" },
    { emoji: "⚠️", label: "Unsafe" }
  ];

  const handleSaveCheckIn = async (isAutoSubmit: boolean = false) => {
    // Validate that an emoji is selected
    if (!selectedEmoji) {
      if (!isAutoSubmit) {
        alert("Please select a feeling before saving.");
      }
      return;
    }
    
    console.log("Saved check-in:", selectedEmoji);
    
    // Submit to Supabase
    const result = await submitCheckIn({
      emoji: selectedEmoji.emoji,
      feeling: selectedEmoji.label,
      userEmail: undefined, // TODO: Add user email when auth is implemented
      userId: undefined, // TODO: Add user ID when auth is implemented
    });
    
    if (result.success) {
      console.log('✅ Check-in saved to Supabase with ID:', result.id);
    } else {
      console.warn('⚠️ Check-in not saved to Supabase:', result.error);
    }
    
    // Save to Firebase as backup
    const db = getFirebaseDB();
    
    if (db) {
      try {
        const checkInsCollection = collection(db, 'checkIns');
        
        const checkInData = {
          emoji: selectedEmoji.emoji,
          feeling: selectedEmoji.label,
          timestamp: serverTimestamp(),
          createdAt: new Date().toISOString()
        };

        const docRef = await addDoc(checkInsCollection, checkInData);
        console.log('✅ Check-in feedback saved to Firebase with ID:', docRef.id);
      } catch (error) {
        console.error('❌ Error saving check-in to Firebase:', error);
        // Continue even if Firebase save fails
      }
    }
    
    // Also save to localStorage for backward compatibility
    const checkInLog = {
      emoji: selectedEmoji.emoji,
      feeling: selectedEmoji.label,
      timestamp: Date.now()
    };
    
    const existingLogs = localStorage.getItem("checkInLogs");
    const logs = existingLogs ? JSON.parse(existingLogs) : [];
    logs.push(checkInLog);
    localStorage.setItem("checkInLogs", JSON.stringify(logs));
    
    // Close the check-in form
    setShowCheckIn(false);
    setSelectedEmoji(null);
    
    // Show thank you message with heart (only for manual save, not auto-submit)
    if (!isAutoSubmit) {
      setShowThankYouPopup(true);
      setTimeout(() => {
        setShowThankYouPopup(false);
      }, 3000);
    }
  };

  const handleQuickObservationSubmit = async (observation: string, additionalContext: string, topic: string, actionType: 'IMPROVE' | 'SUSTAIN', sentiment: string, location?: string) => {
    console.log("Quick Observation submitted:", { observation, additionalContext, topic, actionType, sentiment, location });
    
    // Add to Recent Activity
    addActivity({
      observation,
      additionalContext,
      topic,
      actionType,
      sentiment
    });
    
    // Submit to Supabase and send email
    const result = await submitQuickObservation({
      observation,
      additionalContext,
      topic,
      userEmail: undefined, // TODO: Add user email when auth is implemented
      userId: undefined, // TODO: Add user ID when auth is implemented
    });

    // Always send email to cara@oratf.info automatically
    const emailSubject = encodeURIComponent(`Quick Observation: ${topic}`);
    const emailBody = encodeURIComponent(
      `Topic: ${topic}\n\nObservation or Issue:\n${observation}\n\nAdditional Context:\n${additionalContext || 'N/A'}\n\nAction Type: ${actionType}\nSentiment: ${sentiment}${location ? `\n\nLocation: ${location}` : ''}\n\n---\n${result.success ? `Observation ID: ${result.id}` : 'Sent from ORA Leadership Agent'}`
    );
    const mailtoLink = `mailto:cara@oratf.info?subject=${emailSubject}&body=${emailBody}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    setShowQuickObservationPopup(false);
    
    // Show thank you popup for 4 seconds
    setShowThankYouPopup(true);
    setTimeout(() => {
      setShowThankYouPopup(false);
    }, 4000);
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const aspectRatio = video.videoWidth / video.videoHeight;
      
      // Log video dimensions for debugging
      console.log('Video native dimensions:', {
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: aspectRatio,
        videoSource: currentVideo
      });
      
      // Container width is max 415px (modal width)
      const containerWidth = 415;
      
      // Calculate ideal height based on aspect ratio
      let calculatedHeight = containerWidth / aspectRatio;
      
      // Clamp height between 200px and 233.43px to ensure video never extends beyond screen
      // Lower max height ensures full video visibility without cropping on all devices
      const MIN_HEIGHT = 200;
      const MAX_HEIGHT = 233.43;
      calculatedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, calculatedHeight));
      
      console.log('Container dimensions:', {
        width: containerWidth,
        height: calculatedHeight
      });
      
      setVideoContainerHeight(calculatedHeight);
    }
  };

  const handleVideoClick = () => {
    setIsVideoExpanded(true);
    setTimeout(() => {
      if (expandedVideoRef.current) {
        expandedVideoRef.current.currentTime = videoRef.current?.currentTime || 0;
        expandedVideoRef.current.play().catch(err => {
          console.log("Video autoplay prevented:", err);
        });
      }
    }, 50);
  };

  const handleExpandVideoClick = () => {
    setIsVideoExpanded(true);
    if (expandedVideoRef.current) {
      expandedVideoRef.current.load(); // Load the new source
      expandedVideoRef.current.play().catch(err => {
        console.log("Video autoplay prevented:", err);
      });
    }
  };

  const handleCloseExpandedVideo = () => {
    setIsVideoExpanded(false);
  };

  // Helper function to render message content with clickable links
  // Parses markdown-style links: [text](url) and converts them to clickable links
  const renderMessageContent = (content: string) => {
    // Regular expression to find markdown-style links [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: JSX.Element[] = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(content)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        const textBefore = content.substring(lastIndex, match.index);
        parts.push(<span key={`text-${lastIndex}`} className="!text-gray-700">{textBefore}</span>);
      }

      // Add the link
      const linkText = match[1];
      const linkUrl = match[2];
      parts.push(
        <a
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-800 underline hover:opacity-70"
        >
          {linkText}
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after the last link
    if (lastIndex < content.length) {
      const remainingText = content.substring(lastIndex);
      parts.push(<span key={`text-${lastIndex}`} className="!text-gray-700">{remainingText}</span>);
    }

    // If no links were found, return the original content wrapped in span
    // Otherwise return all parts wrapped in a fragment
    return parts.length === 0 ? <span className="!text-gray-700">{content}</span> : <>{parts}</>;
  };

  if (!isOpen) return null;

  return (
    <>
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 ${noBackdrop ? 'pointer-events-none' : 'bg-black/80'}`}>
      {/* RULE: Agent modal always displays in light mode - Mobile-only width (393px max on all screens) */}
      <div className="bg-white w-[393px] max-w-full rounded-xl shadow-xl relative flex flex-col overflow-y-auto" style={{height: '860px', maxHeight: '95vh'}}>
        {/* Vertically Stacked Icon Buttons - Right Side */}
        <div className="absolute top-[12px] right-4 flex flex-col gap-3 z-50">
          {/* Close Button (top) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="bg-white/45 rounded-full p-2 hover:bg-white/60 transition-colors shadow-md"
            aria-label="Close"
          >
            <X className="size-4 text-white" />
          </button>
          
          {/* Share Link Button */}
          <button
            onClick={copyShareLink}
            className="bg-white/45 rounded-full p-2 hover:bg-white/60 transition-colors shadow-md"
            aria-label="Share Link"
            title="Copy shareable link for new chat"
          >
            {linkCopied ? (
              <Check className="size-4 text-green-600" />
            ) : (
              <Share2 className="size-4 text-white" />
            )}
          </button>
          
          {/* Dashboard Icon - Analytics */}
          <button
            onClick={() => {
              onClose();
              navigate('/analytics');
            }}
            className="bg-white/45 rounded-full p-2 hover:bg-white/60 transition-colors shadow-md"
            aria-label="Open Analytics Dashboard"
            title="Analytics Dashboard"
          >
            <BarChart3 className="size-4 text-white" />
          </button>

          {/* Next Video Button - Bottom of stack */}
          <button
            onClick={handleNextVideo}
            className="bg-white/45 rounded-full p-2 hover:bg-white/60 transition-colors shadow-md"
            title="Next Video"
          >
            <ChevronRight className="size-4 text-white" />
          </button>
        </div>

        {/* Black Header Section - DRAGGABLE */}
        <div 
          ref={dragHandleRef}
          className="relative bg-black pt-1 sm:cursor-grab sm:active:cursor-grabbing select-none" 
          style={{ height: videoContainerHeight, userSelect: 'none' }}
          onMouseDown={handleMouseDown}
        >
          {/* Tap area at top to go to previous video */}
          <button
            onClick={handlePreviousVideo}
            className="absolute top-0 left-0 right-0 h-[60px] z-10 cursor-pointer"
            title="Tap top to go to previous video"
            aria-label="Previous video"
          />
          
          {/* Previous Video Button - Left side */}
          <button
            onClick={handlePreviousVideo}
            className="absolute left-4 top-[146px] z-50 p-2 rounded-full bg-white/45 hover:bg-white/60 transition-colors"
            title="Previous Video"
          >
            <ChevronLeft className="size-4 text-white" />
          </button>
          
          <video
            ref={videoRef}
            className="w-full h-full object-contain cursor-pointer"
            autoPlay
            muted={false}
            playsInline
            controls
            onClick={handleVideoClick}
            onLoadedMetadata={handleVideoLoadedMetadata}
            onEnded={handleVideoEnded}
            onPlay={() => {
              if (videoRef.current) {
                setVideoStartTime(videoRef.current.currentTime);
              }
            }}
            onPause={() => {
              if (videoRef.current && videoStartTime > 0) {
                const duration = Math.floor(videoRef.current.currentTime - videoStartTime);
                if (duration > 0) {
                  trackVideoView('Leadership Agent Video', duration);
                }
                setVideoStartTime(videoRef.current.currentTime);
              }
            }}
          >
            <source src={currentVideo} type="video/mp4" />
          </video>
        </div>

        {/* Draggable Header Bar - Outside scrollable content for proper drag detection */}
        <div 
          className="bg-gradient-to-b from-gray-50 to-white z-20 py-2 px-3 border-b border-gray-200 cursor-grab active:cursor-grabbing select-none hover:bg-gray-100 transition-colors pointer-events-auto"
          style={{ userSelect: 'none', touchAction: 'none' }}
          onMouseDown={handleMouseDown}
          title="Drag to move the agent"
        >
          <div className="flex items-center justify-center gap-2 pointer-events-none">
            <div className="w-8 h-1 bg-gray-300 rounded-full"></div>
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 overflow-y-auto pt-0 pb-4 px-2 space-y-[8px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
          
          {showCheckIn ? (
            /* Check-in View */
            <div className="space-y-2 -mt-[3px]">
              {/* Header */}
              <div className="flex items-center justify-between mt-6">
                <h2 className="font-semibold text-sm">Checking-in</h2>
                <button
                  onClick={() => handleSaveCheckIn(false)}
                  disabled={!selectedEmoji}
                  className={`px-4 py-[2px] rounded-lg text-sm transition-colors -translate-y-[3px] ${
                    selectedEmoji 
                      ? 'bg-[#8DB600] hover:bg-[#7BA600] text-white cursor-pointer' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              </div>

              {/* Question */}
              <p className="text-xs text-gray-700 text-center py-1">
                How are you feeling about AI enabled change?
              </p>

              {/* Compact Emoji Grid - 4 columns for mobile */}
              <div className="grid grid-cols-4 gap-1.5">
                {emojiOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedEmoji(option)}
                    className={`flex flex-col items-center justify-center px-1 py-1 rounded-lg border-2 transition-all ${
                      selectedEmoji?.label === option.label
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-300 bg-white hover:border-gray-400"
                    }`}
                  >
                    <span className="text-lg mb-0.5">{option.emoji}</span>
                    <span className="text-[8px] text-black font-medium text-center leading-tight">
                      {option.label}
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Selection Feedback */}
              {selectedEmoji ? (
                <div className="text-center mt-2">
                  <p className="text-xs text-green-600 font-medium">
                    ✓ {selectedEmoji.emoji} {selectedEmoji.label} selected. Click Save to record.
                  </p>
                </div>
              ) : (
                <div className="text-center mt-2">
                  <p className="text-xs text-gray-500">
                    Select a feeling above, then click Save
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Fixed Topic Selection Area */}
              <div className="sticky top-0 bg-white z-10 pb-[8px] pt-[8px]">
                {showGreeting && (
                  <p className="!text-[#000000] text-sm mb-[8px]">
                    Hello! I can share real lessons learned. Tap a topic!
                  </p>
                )}

                {/* Topic Buttons Grid */}
                <div className="grid grid-cols-2 gap-x-[5px] gap-y-[10px]">
                  {aiTopics.slice(0, 6).map((topic) => {
                    // Specific topics with reduced padding (4px less inner padding)
                    const isReducedPadding = [
                      "Applied AI for Transformation",
                      "Human Health & Fitness",
                      "AI Blind Spots & Pitfalls",
                      "KPI & ROI"
                    ].includes(topic);
                    
                    return (
                      <button
                        key={topic}
                        className={`py-[2px] ${isReducedPadding ? 'px-[7px]' : 'px-[11px]'} bg-gray-600 hover:bg-gray-700 rounded-sm text-[10px] transition-colors text-center whitespace-nowrap overflow-hidden !text-white font-medium`}
                        onClick={() => handleTopicClick(topic)}
                      >
                        {topic}
                      </button>
                    );
                  })}
                  {/* Last row with 3 small buttons inline */}
                  <div className="col-span-2 flex gap-[5px]">
                    {aiTopics.slice(6).map((topic) => {
                      // Check if this is the "Training" button (reduced padding)
                      const isReducedPadding = topic === "Training";
                      
                      return (
                        <button
                          key={topic}
                          className={`flex-1 py-[2px] ${isReducedPadding ? 'px-[7px]' : 'px-[11px]'} bg-gray-600 hover:bg-gray-700 rounded-sm text-[10px] transition-colors text-center whitespace-nowrap !text-white font-medium`}
                          onClick={() => handleTopicClick(topic)}
                        >
                          {topic}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Messages Area - Scrollable */}
              <div className="space-y-[6px]">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}
                  >
                    <div
                      className={`px-2 py-[0px] rounded-sm text-sm text-black ${
                        message.role === "user" ? "bg-purple-100" : "bg-gray-100"
                      }`}
                    >
                      {renderMessageContent(message.content)}
                    </div>
                    {/* Thumbs up/down feedback for assistant messages */}
                    {/* RULE: Thumbs buttons are gray, not white */}
                    {message.role === "assistant" && message.id && (
                      <div className="flex items-center gap-1 mt-[2px]">
                        <button
                          onClick={() => handleFeedback(message.id!, "thumbs_up")}
                          className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                            message.feedback === "thumbs_up" ? "bg-green-100 text-green-600" : "text-gray-500"
                          }`}
                          aria-label="Helpful"
                        >
                          <ThumbsUp className="size-3" />
                        </button>
                        <button
                          onClick={() => handleFeedback(message.id!, "thumbs_down")}
                          className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                            message.feedback === "thumbs_down" ? "bg-red-100 text-red-600" : "text-gray-500"
                          }`}
                          aria-label="Not helpful"
                        >
                                                   <ThumbsDown className="size-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="px-2 py-[0px] rounded-sm text-sm bg-gray-100 text-gray-700">
                      <span className="animate-pulse">...</span>
                    </div>
                  </div>
                )}
                               <div ref={messagesEndRef} />
              </div>
            </>
          )}
        </div>

        {/* Free Questions Popup - Floating overlay */}
        {showFreeQuestionsPopup && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 animate-fade-in">
            <div className="bg-purple-500/60 !text-[#000000] px-4 py-[6px] rounded-full text-xs font-semibold shadow-lg">
              10 free questions remaining
            </div>
          </div>
        )}

        {/* Input Section */}
        <div className="border-t border-gray-200 pt-[14px] pr-[14px] pb-[14px] pl-[10px]">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsRecentActivityOpen(true)}
              className="p-1.5 hover:bg-yellow-50 rounded-lg transition-colors relative group shrink-0"
              aria-label="Recent Activity"
            >
              <AlertTriangle className="size-5 fill-orange-300 text-orange-400" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-[4px] bg-gray-200 text-black text-xs rounded whitespace-nowrap hidden group-hover:block pointer-events-none">
                Recent
              </div>
            </button>
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Ask a question"
                className="w-full pl-4 pr-12 py-[6px] bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => chatVoice.toggleRecording()}
              >
                <Mic className="size-4 text-gray-500" />
              </button>
            </div>
            <button 
              onClick={() => navigate('/pricing')}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors relative group shrink-0"
              aria-label="Upgrade"
            >
              <Settings className="size-4 text-gray-600" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-[4px] bg-gray-200 text-black text-xs rounded whitespace-nowrap hidden group-hover:block pointer-events-none">
                Upgrade
              </div>
            </button>
            <button className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors shrink-0" onClick={handleSendMessage}>
              <Send className="size-4 text-black" />
            </button>
          </div>
          
          {/* Decorative Watermark Text */}
          <div className="text-gray-400 text-[7px] sm:text-[8px] tracking-[0.15em] sm:tracking-[0.2em] font-bold opacity-30 text-center mt-[6px] whitespace-nowrap">
            AGENTIC AI LEADERSHIP FOR MEASURABLE TRANSFORMATION
          </div>
        </div>
      </div>
    </div>

    {/* Expanded Video Modal Overlay */}
    {isVideoExpanded && (
      <div 
        className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
        onClick={handleCloseExpandedVideo}
      >
        <div className="relative w-full max-w-6xl aspect-video">
          {/* Close Button */}
          <button
            onClick={handleCloseExpandedVideo}
            className="absolute -top-12 right-0 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors z-10"
            aria-label="Close expanded video"
          >
            <X className="size-6 text-white" />
          </button>
          
          {/* Expanded Video Player */}
          <video
            ref={expandedVideoRef}
            className="w-full h-full object-contain rounded-lg"
            autoPlay
            playsInline
            controls
            onClick={(e) => e.stopPropagation()}
            onEnded={handleVideoEnded}
          >
            <source src={currentVideo} type="video/mp4" />
          </video>
          
          {/* Tap/Click instruction for mobile */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-4 py-[6px] rounded-full pointer-events-none">
            Tap outside to close
          </div>
        </div>
      </div>
    )}

    {/* Report Issue Modal */}
    <ReportIssueModal 
      isOpen={isReportIssueOpen} 
      onClose={() => {
        setIsReportIssueOpen(false);
        setSubmittedObservation("");
      }}
      subscriberEmail={localStorage.getItem('subscriberEmail') || ''}
      initialObservation={submittedObservation}
      onSaveReport={(data) => {
        console.log('Report saved:', data);
        // Open ActionableSummary with the report data
        setShowActionableSummary(true);
      }}
    />

    {/* Actionable Summary */}
    <ActionableSummary
      isOpen={showActionableSummary}
      onClose={() => setShowActionableSummary(false)}
      observation={observationData.text}
      recommendation={DEFAULT_RECOMMENDATION}
      topic={observationData.topic}
      eventType={observationData.type}
      sentiment={observationData.sentiment}
      location={observationData.location}
      date={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
    />

    {/* Quick Observation Popup */}
    <QuickObservationPopup
      isOpen={showQuickObservationPopup}
      onClose={() => setShowQuickObservationPopup(false)}
      onSubmit={handleQuickObservationSubmit}
    />
    
    {/* Thank You Popup */}
    {showThankYouPopup && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-12 flex flex-col items-center justify-center space-y-6 animate-fadeIn">
          <div className="text-6xl animate-pulse">❤️</div>
          <h2 className="text-3xl font-bold text-gray-900">Thank You!</h2>
        </div>
      </div>
    )}
    
    {/* Recent Activity */}
    <RecentActivity 
      isOpen={isRecentActivityOpen} 
      onClose={() => setIsRecentActivityOpen(false)}
    />
    </>
  );
}